import React from 'react';
import { useDressStore } from '../../stores/useDressStore';
import { useUserStore } from '../../stores/useUserStore';
import { dressList, dressNames } from '../../data/dressList';

interface DressModalProps {
  visible: boolean;
  onClose: () => void;
  lang: 'ja' | 'en';
  onShowMessage?: (message: string, duration?: number) => void;
  onShowConfirm?: (message: string, onConfirm: () => void) => void;
}

export const DressModal: React.FC<DressModalProps> = ({
  visible,
  onClose,
  lang,
  onShowMessage,
  onShowConfirm
}) => {
  const { selectedDress, dressUnlocks, selectDress, unlockDress } = useDressStore();
  const { donuts, spendDonut } = useUserStore();

  const getDressName = (dressId: string) => {
    const dress = dressList.find(d => d.id === dressId);
    if (!dress) return '';
    return dressNames[dress.nameKey]?.[lang] || dress.nameKey;
  };

  const isUnlocked = (dress: typeof dressList[0]) => {
    return dressUnlocks[dress.id] || dress.unlocked;
  };

  const handleDressClick = (dress: typeof dressList[0]) => {
    if (isUnlocked(dress)) {
      if (selectedDress !== dress.id) {
        onShowConfirm?.(
          lang === 'ja'
            ? `${dressNames[dress.nameKey]?.[lang]} に着替えますか？`
            : `Change into ${dressNames[dress.nameKey]?.[lang]}?`,
          () => {
            selectDress(dress.id);
            onClose();
          }
        );
      }
    } else {
      // Purchase flow
      if (donuts < dress.price) {
        onShowMessage?.(
          lang === 'ja' ? 'ドーナツが足りないよ...' : 'Not enough donuts...'
        );
        return;
      }
      const name = dressNames[dress.nameKey]?.[lang] || dress.nameKey;
      onShowConfirm?.(
        lang === 'ja'
          ? `${name}を${dress.price}ドーナツで購入しますか？`
          : `Buy ${name} for ${dress.price} donuts?`,
        () => {
          if (spendDonut(dress.price)) {
            unlockDress(dress.id);
            onShowMessage?.(
              lang === 'ja'
                ? '新しい衣装を手に入れた！'
                : 'Got a new outfit!'
            );
          }
        }
      );
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 glass-overlay z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-panel p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">
            {lang === 'ja' ? '着せ替え' : 'Dress Up'}
          </h2>
          <span className="text-sm font-bold px-3 py-1 rounded-full"
            style={{ color: '#ffd700', background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
            {donuts} donut{donuts !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Current outfit */}
        <div className="mb-4 p-3 bg-pink-500/10 border border-pink-500/15 rounded-xl">
          <p className="text-sm text-pink-200">
            {lang === 'ja' ? '現在の衣装' : 'Current Outfit'}:
            <span className="font-bold ml-1">{getDressName(selectedDress)}</span>
          </p>
        </div>

        {/* Dress list - Show all items */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {dressList.map((dress) => {
            const isCurrent = selectedDress === dress.id;
            const unlocked = isUnlocked(dress);
            const canAfford = donuts >= dress.price;

            return (
              <div
                key={dress.id}
                className={`relative border rounded-xl p-3 cursor-pointer transition-all ${
                  isCurrent
                    ? 'bg-pink-500/10 border-pink-500/30'
                    : unlocked
                      ? 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'
                      : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.06]'
                }`}
                onClick={() => handleDressClick(dress)}
              >
                <div className="relative aspect-square bg-white/[0.06] rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                  {dress.thumbnail ? (
                    <img
                      src={dress.thumbnail}
                      alt={getDressName(dress.id)}
                      className={`w-full h-full object-cover rounded ${!unlocked ? 'opacity-40' : ''}`}
                    />
                  ) : (
                    <span className="text-4xl">👗</span>
                  )}
                  {/* Lock overlay */}
                  {!unlocked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-8 h-8 text-white/70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" opacity="0.6" />
                        <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                  )}
                </div>
                <h3 className={`font-medium text-sm text-center ${unlocked ? 'text-white/90' : 'text-white/50'}`}>
                  {dressNames[dress.nameKey]?.[lang]}
                </h3>
                {isCurrent && (
                  <div className="text-center mt-1">
                    <span className="text-pink-400 text-sm">✓</span>
                  </div>
                )}
                {/* Price badge for locked items */}
                {!unlocked && (
                  <div className="text-center mt-1">
                    <span className={`text-xs font-bold ${canAfford ? 'text-yellow-400' : 'text-red-400/70'}`}>
                      {dress.price} donut{dress.price !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full py-2 px-4 glass-btn transition-colors"
        >
          {lang === 'ja' ? '閉じる' : 'Close'}
        </button>
      </div>
    </div>
  );
};
